import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Multi-step "add / edit certification" wizard used in two places:
//   1. the onboarding modal (#modal-content frame)
//   2. the members CRUD slideover (#slideover-content frame)
//
// One client-side form, three panels (provider → details + uploads →
// declaration). Direct uploads happen as files are picked (mirroring
// edit_full_photo_controller): each upload produces a blob signed_id that is
// carried into the form via a hidden input, so the single POST/PATCH on Save
// already has every attachment. Existing attachments (edit mode) are rendered
// server-side with their blob signed_ids so they re-submit unchanged — and
// removing one simply drops its hidden input (Active Storage replaces the whole
// collection on assign, so the dropped blob detaches).
//
// Panels are toggled with the `hidden` class rather than HTML `required`
// attributes: a `required` field inside a hidden panel makes the browser throw
// "An invalid form control is not focusable" on submit. Validation is done in JS
// on Next and on Save instead.
export default class extends Controller {
  static targets = [
    "panel",
    "backBtn",
    "nextBtn",
    "submitBtn",
    "providerError",
    "pdfPreview",
    "pdfProgress",
    "pdfRemoveFlag",
    "imagesPreviews",
    "imagesProgress"
  ]

  static values = {
    directUploadUrl: String,
    current: { type: Number, default: 0 }
  }

  connect() {
    // Start on the first panel carrying a server-rendered field error (edit /
    // failed submit), otherwise the first panel.
    const errored = this.panelTargets.findIndex((p) => p.querySelector(".form-control.error"))
    this.showStep(errored >= 0 ? errored : this.currentValue)
  }

  // --- Step navigation ---

  next() {
    if (!this.validateStep(this.currentValue)) return
    if (this.currentValue < this.lastIndex) this.showStep(this.currentValue + 1)
  }

  back() {
    if (this.currentValue > 0) this.showStep(this.currentValue - 1)
  }

  submit(event) {
    for (let i = 0; i <= this.lastIndex; i++) {
      if (!this.validateStep(i)) {
        event.preventDefault()
        this.showStep(i)
        return
      }
    }
  }

  showStep(index) {
    this.currentValue = index
    this.panelTargets.forEach((panel, i) => panel.classList.toggle("hidden", i !== index))

    const isFirst = index === 0
    const isLast = index === this.lastIndex
    if (this.hasBackBtnTarget) this.backBtnTarget.classList.toggle("hidden", isFirst)
    if (this.hasNextBtnTarget) this.nextBtnTarget.classList.toggle("hidden", isLast)
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.classList.toggle("hidden", !isLast)

    const scroller = this.element.querySelector(".form-fields")
    if (scroller) scroller.scrollTop = 0
  }

  get lastIndex() {
    return this.panelTargets.length - 1
  }

  // --- Validation ---

  validateStep(index) {
    const panel = this.panelTargets[index]
    if (!panel) return true
    let firstInvalid = null

    const radios = panel.querySelectorAll("input[type=radio][data-provider-radio]")
    if (radios.length && ![...radios].some((r) => r.checked)) {
      if (this.hasProviderErrorTarget) this.providerErrorTarget.classList.remove("hidden")
      firstInvalid ||= radios[0]
    }

    for (const field of panel.querySelectorAll("[data-required-field]")) {
      if (field.value.trim()) {
        this.clearFieldError(field)
      } else {
        this.showFieldError(field, `${this.fieldLabel(field)} is required.`)
        firstInvalid ||= field
      }
    }

    if (firstInvalid) {
      firstInvalid.focus({ preventScroll: true })
      firstInvalid.scrollIntoView?.({ block: "center", behavior: "smooth" })
      return false
    }
    return true
  }

  // Clear a field's error the moment the user starts correcting it.
  fieldInput(event) {
    this.clearFieldError(event.target)
  }

  clearProviderError() {
    if (this.hasProviderErrorTarget) this.providerErrorTarget.classList.add("hidden")
  }

  // Mirror the server-side FormInput error markup: a red border on the control
  // plus a <p class="mt-1 text-sm text-red-600"> directly after it.
  showFieldError(field, message) {
    field.classList.add("error")
    let msg = field.nextElementSibling
    if (!this.isErrorNode(msg)) {
      msg = document.createElement("p")
      msg.className = "mt-1 text-sm text-red-600"
      field.insertAdjacentElement("afterend", msg)
    }
    msg.textContent = message
  }

  clearFieldError(field) {
    field.classList.remove("error")
    const msg = field.nextElementSibling
    if (this.isErrorNode(msg)) msg.remove()
  }

  isErrorNode(node) {
    return node && node.tagName === "P" && node.classList.contains("text-red-600")
  }

  fieldLabel(field) {
    const label = field.closest("div")?.querySelector("label")
    return label?.textContent?.trim() || "This field"
  }

  // --- PDF certificate (single) ---

  pdfSelected(event) {
    const file = (event.target.files || [])[0]
    event.target.value = ""
    if (!file) return

    const progress = this.buildProgress(file.name)
    this.pdfProgressTarget.appendChild(progress.el)

    const upload = new DirectUpload(file, this.directUploadUrlValue, this.progressDelegate(progress))
    upload.create((error, blob) => {
      progress.el.remove()
      if (error) {
        this.flashError(this.pdfProgressTarget, `Couldn't upload ${file.name}`)
        return
      }
      // Replacing the PDF supersedes any pending removal of an existing one.
      if (this.hasPdfRemoveFlagTarget) this.pdfRemoveFlagTarget.value = ""
      this.pdfPreviewTarget.innerHTML = this.pdfChipHtml(file.name, blob.signed_id)
    })
  }

  removePdf(event) {
    const chip = event.currentTarget.closest("[data-pdf-chip]")
    if (chip?.dataset.existing === "true" && this.hasPdfRemoveFlagTarget) {
      this.pdfRemoveFlagTarget.value = "1"
    }
    if (chip) chip.remove()
  }

  // --- Certificate images (multiple) ---

  imagesSelected(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    files.forEach((file) => this.uploadImage(file))
  }

  uploadImage(file) {
    const localUrl = URL.createObjectURL(file)
    const progress = this.buildProgress(file.name)
    this.imagesProgressTarget.appendChild(progress.el)

    const upload = new DirectUpload(file, this.directUploadUrlValue, this.progressDelegate(progress))
    upload.create((error, blob) => {
      progress.el.remove()
      if (error) {
        this.flashError(this.imagesProgressTarget, `Couldn't upload ${file.name}`)
        return
      }
      this.imagesPreviewsTarget.insertAdjacentHTML("beforeend", this.imageChipHtml(localUrl, blob.signed_id))
    })
  }

  removeImage(event) {
    const item = event.currentTarget.closest("[data-image-chip]")
    if (item) item.remove()
  }

  // --- Direct-upload progress helpers ---

  progressDelegate(progress) {
    return {
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return
          const percent = Math.round((event.loaded / event.total) * 100)
          progress.bar.style.width = `${percent}%`
          progress.text.textContent = `${percent}%`
        })
      }
    }
  }

  buildProgress(name) {
    const el = document.createElement("div")
    el.className = "flex items-center gap-2 text-xs text-gray-600 mt-2"
    el.innerHTML = `
      <span class="truncate max-w-[12rem]">${this.escape(name)}</span>
      <div class="flex-1 bg-gray-200 rounded-full h-1.5">
        <div class="bg-blue-600 h-1.5 rounded-full transition-all duration-200" style="width: 0%" data-progress-bar></div>
      </div>
      <span data-progress-text>0%</span>
    `
    return {
      el,
      bar: el.querySelector("[data-progress-bar]"),
      text: el.querySelector("[data-progress-text]")
    }
  }

  flashError(container, message) {
    const el = document.createElement("p")
    el.className = "mt-2 text-xs text-red-600"
    el.textContent = message
    container.appendChild(el)
    setTimeout(() => el.remove(), 4000)
  }

  // --- Chip markup ---

  pdfChipHtml(name, signedId) {
    return `
      <div data-pdf-chip class="flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2">
        <input type="hidden" name="member_certification[pdf_certificate]" value="${this.escape(signedId)}">
        <svg class="h-4 w-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
        <span class="flex-1 min-w-0 truncate text-sm text-gray-700">${this.escape(name)}</span>
        <button type="button" data-action="click->certification-form#removePdf" class="text-gray-400 hover:text-red-600 shrink-0" aria-label="Remove file">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `
  }

  imageChipHtml(url, signedId) {
    return `
      <div data-image-chip class="relative group">
        <input type="hidden" name="member_certification[certificate_images][]" value="${this.escape(signedId)}">
        <img src="${this.escape(url)}" class="h-24 w-24 object-cover rounded-lg border border-gray-300 bg-gray-50">
        <button type="button" data-action="click->certification-form#removeImage"
                class="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image">&times;</button>
      </div>
    `
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value
    return div.innerHTML.replace(/"/g, "&quot;")
  }
}
